import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGoBack } from "@/hooks/use-go-back";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Pencil, Search, Trash2 } from "lucide-react";
import { deleteTeam, getTeam } from "@/features/supply-config/teams-api";
import { ContentLayout } from "@/components/common/content-layout";
import { HasAccess } from "@/components/common/has-access";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { codeToFlag } from "@/features/regions/utils/code-to-flag";

function Meta({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <div className="text-sm text-gray-900 mt-0.5">{value || "—"}</div>
    </div>
  );
}

export default function TeamDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const goBack = useGoBack("..");
  const qc = useQueryClient();
  const [search, setSearch] = useState("");

  const { data: team, isLoading, isFetching, error } = useQuery({
    queryKey: ["team", id],
    queryFn: () => getTeam(id!),
    enabled: !!id,
  });

  const remove = useMutation({
    mutationFn: () => deleteTeam(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["teams"] });
      toast({ title: "Team deleted" });
      navigate("..");
    },
    onError: (e: any) =>
      toast({
        title: "Error",
        description: e.response?.data?.message || "Failed",
        variant: "destructive",
      }),
  });

  const professionals = team?.professionals ?? [];
  const filteredPros = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return professionals;
    return professionals.filter((p) => p.name.toLowerCase().includes(q));
  }, [professionals, search]);

  return (
    <ContentLayout
      data={team ? [team] : []}
      isLoading={isLoading}
      isFetching={isFetching}
      error={error}
      contentClassName="space-y-6"
      errorMessage="Failed to load team"
      emptyState={{ message: "Team not found" }}
    >
      {team && (
        <div className="w-full space-y-6">
          <button
            type="button"
            onClick={goBack}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" /> Teams
          </button>

          {/* Header */}
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold text-gray-900">
                {team.name}
              </h1>
              <div className="flex items-center gap-2 mt-2">
                {team.region_rel && (
                  <Badge variant="outline" className="text-[10px] uppercase">
                    {codeToFlag(team.region_rel.code)} {team.region_rel.code}
                  </Badge>
                )}
                <Badge
                  variant={team.status === "ACTIVE" ? "active" : "inactive"}
                  className="text-[10px] uppercase"
                >
                  {team.status}
                </Badge>
              </div>
            </div>
            <HasAccess permission="write">
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("edit")}
                >
                  <Pencil className="h-4 w-4 mr-1" /> Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    window.confirm(`Delete team "${team.name}"?`) &&
                    remove.mutate()
                  }
                >
                  <Trash2 className="h-4 w-4 mr-1 text-red-500" /> Delete
                </Button>
              </div>
            </HasAccess>
          </div>

          {/* Overview */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Meta
                  label="Products"
                  value={team.products?.map((p) => p.name).join(", ")}
                />
                <Meta
                  label="Region"
                  value={
                    team.region_rel
                      ? `${codeToFlag(team.region_rel.code)} ${team.region_rel.display_name}`
                      : "—"
                  }
                />
                <Meta
                  label="Status"
                  value={
                    <Badge
                      variant={team.status === "ACTIVE" ? "active" : "inactive"}
                    >
                      {team.status}
                    </Badge>
                  }
                />
                <Meta
                  label="Supported languages"
                  value={
                    team.supported_languages?.length ? (
                      <div className="flex flex-wrap gap-1">
                        {team.supported_languages.map((l) => (
                          <Badge key={l} variant="outline">
                            {l}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      "—"
                    )
                  }
                />
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            {/* Professionals */}
            <Card>
              <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2">
                <CardTitle className="text-base">
                  Professionals ({professionals.length})
                </CardTitle>
                {professionals.length > 8 && (
                  <div className="relative w-40">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Filter…"
                      className="h-8 pl-7 text-sm"
                    />
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {professionals.length === 0 ? (
                  <p className="text-sm text-gray-400">No professionals.</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5 max-h-72 overflow-y-auto pr-1">
                    {filteredPros.map((p) => (
                      <Badge key={p.id} variant="secondary">
                        {p.name}
                      </Badge>
                    ))}
                    {filteredPros.length === 0 && (
                      <p className="text-sm text-gray-400">No matches.</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Managed events */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">
                  Managed events ({team.managed_events?.length ?? 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {team.managed_events?.length ? (
                  <div className="border border-gray-200 rounded-md divide-y max-h-72 overflow-y-auto">
                    {team.managed_events.map((m) => (
                      <div key={m.id} className="px-3 py-2 text-sm">
                        {m.name}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">No managed events yet.</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </ContentLayout>
  );
}
